import PropTypes from 'prop-types';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import { connect } from 'react-redux';

import { SharedPagination } from '../../shared/SharedPagination/SharedPagination';
import { SbRespRefineMenuSelector } from '../SbRespRefineMenuSelector/SbRespRefineMenuSelector';
import { SbRespSortItems } from '../SbRespSortItems/SbRespSortItems';
import { SbRespPageSize } from '../SbRespPageSize/SbRespPageSize';
import { SbRespBrowseHeader } from '../SbRespBrowseHeader/SbRespBrowseHeader';
import { SbRespAppliedFilters } from '../SbRespAppliedFilters/SbRespAppliedFilters';
import { SbRespSearchResultContainer } from '../SbRespSearchResultContainer/SbRespSearchResultContainer';
import { SbSharedRelatedItems } from '../SbSharedRelatedItems/SbSharedRelatedItems';
import { SbSharedRecentlySoldItems } from '../SbSharedRecentlySoldItems/SbSharedRecentlySoldItems';

import { VERTICALS } from '../../constants/uriConstants';
import { pageTypeConstants as pageTypes } from '../../constants/pageTypeConstants';

import styles from './SbRespMainContent.scss';

function SbRespMainContentComponent({
    currency,
    itemSearch,
    auctionsItemSearch,
    viewer,
    user,
    isClient,
    isThinPage,
    relay,
    generatedUriRef,
    showMeasurements,
    page,
    saveSearchTopButton,
}) {
    const { topCategoryL1, displayUriRef, displayMaxNumberOfPages, pageType } = itemSearch;

    const { canShowInternalSellerName, canShowSellerName, canShowStorefrontOnlyBadge } = user || {};

    const isFurniture = topCategoryL1 === VERTICALS.FURNITURE;
    const canShowCreatorName = !canShowSellerName && isFurniture;

    const sharedProps = {
        currency,
        itemSearch,
        viewer,
        user,
        isClient,
    };

    return (
        <div className={styles.mainContentContainer}>
            {
                <SbRespRefineMenuSelector
                    {...sharedProps}
                    uriRef={generatedUriRef}
                    isThinPage={isThinPage}
                />
            }
            <div className={styles.resultsContainer}>
                <div className={styles.resultsHeader}>
                    <div className={styles.sortContainer}>
                        <SbRespSortItems itemSearch={itemSearch} user={user} />
                        <SbRespPageSize itemSearch={itemSearch} />
                    </div>
                    <div>
                        <SbRespBrowseHeader itemSearch={itemSearch} />
                        {saveSearchTopButton}
                    </div>
                </div>
                <SbRespAppliedFilters user={user} currency={currency} itemSearch={itemSearch} />
                <div className={styles.resultsContainer}>
                    {/* Item Results */}
                    <SbRespSearchResultContainer
                        {...sharedProps}
                        showInternalSellerName={canShowInternalSellerName}
                        showStorefrontOnlyBadge={
                            canShowStorefrontOnlyBadge && pageType === pageTypes.DEALER
                        }
                        showSellerName={!!canShowSellerName}
                        showCreatorName={canShowCreatorName}
                        showMeasurements={showMeasurements}
                        relay={relay}
                        auctionsItemSearch={auctionsItemSearch}
                    />
                    {/* Related Items */}
                    {isThinPage && (
                        <>
                            <SbSharedRelatedItems
                                itemSearch={itemSearch}
                                isClient={isClient}
                                viewer={viewer}
                            />
                            <SbSharedRecentlySoldItems
                                itemSearch={itemSearch}
                                isClient={isClient}
                                viewer={viewer}
                            />
                        </>
                    )}

                    {/* Pagination */}
                    <div className={styles.paginationContainer}>
                        <SharedPagination
                            currentPage={page}
                            numberOfPages={displayMaxNumberOfPages}
                            displayUriRef={displayUriRef}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}

SbRespMainContentComponent.propTypes = {
    currency: PropTypes.string.isRequired,
    itemSearch: PropTypes.shape({
        displayUriRef: PropTypes.string.isRequired,
        topCategoryL1: PropTypes.string,
        displayMaxNumberOfPages: PropTypes.number,
        pageType: PropTypes.string,
        appliedFilters: PropTypes.array,
    }).isRequired,
    auctionsItemSearch: PropTypes.object,
    viewer: PropTypes.object.isRequired,
    user: PropTypes.object,
    isClient: PropTypes.bool,
    isThinPage: PropTypes.bool.isRequired,
    relay: PropTypes.object.isRequired,
    generatedUriRef: PropTypes.string,
    showMeasurements: PropTypes.bool,
    page: PropTypes.number,
    saveSearchTopButton: PropTypes.element,
};

const mapStateToProps = ({ displayPreferences, filters }) => ({
    showMeasurements: displayPreferences.showMeasurements,
    generatedUriRef: filters.generatedUriRef,
    page: filters.page,
});

export const SbRespMainContent = createFragmentContainer(
    connect(mapStateToProps)(SbRespMainContentComponent),
    {
        itemSearch: graphql`
            fragment SbRespMainContent_itemSearch on ItemSearchQueryConnection {
                topCategoryL1
                displayUriRef
                pageType
                displayMaxNumberOfPages
                ...SbRespRefineMenuSelector_itemSearch
                ...SbRespSortItems_itemSearch
                ...SbRespPageSize_itemSearch
                ...SbRespBrowseHeader_itemSearch
                ...SbRespSearchResultContainer_itemSearch
                ...SbSharedRelatedItems_itemSearch
                ...SbSharedRecentlySoldItems_itemSearch
                ...SbRespAppliedFilters_itemSearch
            }
        `,
        auctionsItemSearch: graphql`
            fragment SbRespMainContent_auctionsItemSearch on ItemSearchQueryConnection {
                ...SbRespSearchResultContainer_auctionsItemSearch
            }
        `,
        user: graphql`
            fragment SbRespMainContent_user on User {
                canShowSellerName
                canShowInternalSellerName: hasPermission(name: "VIEW_SELLER_NAME")
                canShowStorefrontOnlyBadge: hasPermission(name: "VIEW_STOREFRONT_ONLY_BADGE")
                ...SbRespRefineMenuSelector_user
                ...SbRespSearchResultContainer_user
                ...SbRespAppliedFilters_user
                ...SbRespSortItems_user
            }
        `,
        viewer: graphql`
            fragment SbRespMainContent_viewer on Viewer {
                ...SbRespRefineMenuSelector_viewer
                ...SbRespSearchResultContainer_viewer
                ...SbSharedRelatedItems_viewer
                ...SbSharedRecentlySoldItems_viewer
            }
        `,
    }
);
